import { Box, Divider, Stack, Tab } from '@mui/material';
import { ArrowUpRight } from '@phosphor-icons/react';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import AttachmentsIndex from './attachments';
import InformedIndividualsTab from './attachments/informedIndividualsTab/informedIndividualsTab';
import InvestigatorNotesTab from './attachments/investigatorNotesTab';
import MessagesTab from './attachments/messages';
import { TabsWrapper } from './styled-components';

import reportChatService from '../../../../services/reportChatService';
import {
  setSendRequest,
  setUnreadMessages,
} from '../../../../slices/reportSlice';
import { RootState } from '../../../../utils/store';
import ToastAlert from '../../../common/ToastNotification/toastAlert';

import { ProcessTemplateTab } from '~/pages/customer/reports/report-details/process-management/ProcessesTab';

export type ComponentTabsType = {
  itemKey: string;
  value: string;
  tabKey: number;
};

// import { AuditLogs } from '~/pages/customer/reports/report-details/AuditLogs';
const AuditLogs = React.lazy(() =>
  import('~/pages/customer/reports/report-details/AuditLogs').then(
    (module) => ({ default: module.AuditLogs }),
  ),
);

// import RequestInformationTab from './attachments/requestInformationTab/requestInformationTab';
const RequestInformation = React.lazy(
  () => import('~/pages/customer/reports/report-details/RequestInformation'),
);

const CustomerReportDocuments = () => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const tab = z.string().nullable().catch(null).parse(searchParams.get('tab'));
  const workflow = z.coerce
    .number()
    .nullable()
    .catch(null)
    .parse(searchParams.get('workflow'));
  const taskId = z.coerce
    .number()
    .nullable()
    .catch(null)
    .parse(searchParams.get('task'));

  const componentTabs: Array<ComponentTabsType> = useMemo(
    () => [
      {
        itemKey: 'attachments',
        value: intl.formatMessage({ defaultMessage: 'Attachments' }),
        tabKey: 0,
      },
      {
        itemKey: 'messages',
        value: intl.formatMessage({ defaultMessage: 'Messages' }),
        tabKey: 1,
      },
      {
        itemKey: 'processes',
        value: intl.formatMessage({ defaultMessage: 'Processes' }),
        tabKey: 2,
      },
      {
        itemKey: 'notes',
        value: intl.formatMessage({ defaultMessage: 'Notes' }),
        tabKey: 3,
      },
      {
        itemKey: 'audit logs',
        value: intl.formatMessage({ defaultMessage: 'Audit Logs' }),
        tabKey: 4,
      },
      {
        itemKey: 'request information',
        value: intl.formatMessage({ defaultMessage: 'Request Information' }),
        tabKey: 5,
      },
      {
        itemKey: 'informed individuals',
        value: intl.formatMessage({ defaultMessage: 'Informed Individuals' }),
        tabKey: 6,
      },
    ],
    [intl],
  );

  const reportDetails = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const activeTab = useSelector(
    (state: RootState) => state.rfiMessage.activeTab,
  );

  const sendRequestModalState = useSelector(
    (state: RootState) => state.reports.sendRequest,
  );
  const dispatch = useDispatch();
  const tabsRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState<string>(
    componentTabs[0].itemKey,
  );

  const scrollToBottom = () => {
    tabsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const hasUnreadMessages: boolean = useSelector(
    (state: RootState) => state.reports.hasUnreadMessages,
  );

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    event.preventDefault();
    setSelectedTab(newValue);

    //clear messages if there are unread messages
    if (newValue === 'Messages' && hasUnreadMessages) {
      //clear messages!
      reportChatService
        .patchClearMessages(reportDetails.conversation)
        .then(() => {
          dispatch(setUnreadMessages(false));
        });
    }

    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const setDefaultTab = useCallback(() => {
    if (workflow && taskId) {
      setSelectedTab(componentTabs[2].value);
    } else if (tab) {
      setSelectedTab(componentTabs[4].value);
    } else if (activeTab === 'rfi-message') {
      setSelectedTab(componentTabs[4].value);
    } else {
      setSelectedTab(componentTabs[0].value);
    }
  }, [activeTab, componentTabs, taskId, workflow, tab]);

  useEffect(() => {
    setDefaultTab();
  }, [setDefaultTab]);

  const tabsDisplay = componentTabs.map(
    ({ itemKey, value }: ComponentTabsType) => {
      let additionalStyle = {};
      if (hasUnreadMessages) {
        additionalStyle =
          value === 'Messages'
            ? {
                fontWeight: '500 !important',
                color: 'var(--ri-black) !important',
              }
            : {
                fontWeight: '400 !important',
              };
      }

      return (
        <Tab
          sx={additionalStyle}
          iconPosition={
            value === 'Messages' && hasUnreadMessages ? 'start' : 'top'
          }
          icon={
            value === 'Messages' && hasUnreadMessages ? (
              <Box
                sx={{
                  backgroundColor: '#00CECF',
                  width: '8px',
                  height: '8px',
                  borderRadius: '12px',
                }}
              ></Box>
            ) : (
              ''
            )
          }
          value={value}
          label={value}
          key={`tabKey-${itemKey}`}
          wrapped
        />
      );
    },
  );

  return (
    <Box
      sx={{
        border: '1px solid #D6D6D6',
        borderRadius: '6px',
        height: '100%',
        width: '100%',
        flex: 1,
      }}
      bgcolor='#FFF'
    >
      <Stack
        direction={{
          lg: 'row',
          md: 'column',
          sm: 'column',
          xs: 'column',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <TabsWrapper
            value={selectedTab}
            sx={{
              '& .MuiTabScrollButton-root:first-child': {
                marginLeft: '8px !important',
              },
              '& .MuiTabScrollButton-root:last-child': {
                marginRight: '8px !important',
              },
              '& > .MuiTabs-scroller > .MuiTabs-flexContainer > .MuiButtonBase-root:last-child':
                {
                  borderRight: '0px solid transparent',
                },
              '& > .MuiTabs-scroller > .MuiTabs-flexContainer > .MuiButtonBase-root':
                {
                  borderBottom: '0px solid transparent',
                },
              '& > .MuiTabs-scroller > .MuiTabs-flexContainer > .MuiButtonBase-root.Mui-selected':
                {
                  color: '#00CECF',
                  backgroundColor: '#E0F9F9',
                  borderBottom: '#00CECF',
                },
              '& .MuiTabs-indicator': {
                backgroundColor: '#00CECF !important',
              },
              '& > .MuiTabs-scroller': {
                borderBottom: '1px solid #D6D6D6',
              },
            }}
            defaultValue={selectedTab}
            onChange={handleChangeTab}
            slots={{
              EndScrollButtonIcon: () => {
                return (
                  <Box
                    sx={{
                      color: '#878787',
                      fontSize: '13px',
                      width: '36px',
                      height: '36px',
                      borderRadius: '117px',
                      border: '1px solid #878787',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {'>'}
                  </Box>
                );
              },
              StartScrollButtonIcon: () => {
                return (
                  <Box
                    sx={{
                      color: '#878787',
                      fontSize: '13px',
                      width: '36px',
                      height: '36px',
                      borderRadius: '117px',
                      border: '1px solid #878787',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {'<'}
                  </Box>
                );
              },
            }}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
          >
            {tabsDisplay}

            <Tab
              LinkComponent={({ children }) => (
                <Link
                  to='network-graph'
                  className='flex items-center gap-2 text-zinc-500 font-medium p-4'
                >
                  {children}
                  <ArrowUpRight className='size-4' />
                </Link>
              )}
              href='network-graph'
              value='network graph'
              label={intl.formatMessage({ defaultMessage: 'Network Graph' })}
              key={'tabKey-network-graph'}
              wrapped
            />
          </TabsWrapper>

          <Divider orientation='vertical' flexItem />

          <Box
            sx={{
              height: '550px',
              maxHeight: '550px',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '::-webkit-scrollbar': {
                width: '10px',
              },
              '::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '::-webkit-scrollbar-thumb': {
                bgcolor: 'rgba(155, 155, 155, 0.5)',
                borderRadius: '20px',
                border: 'transparent',
              },
            }}
            ref={tabsRef}
          >
            <Box display={selectedTab === componentTabs[0].value ? '' : 'none'}>
              <AttachmentsIndex />
            </Box>
            <Box display={selectedTab === componentTabs[1].value ? '' : 'none'}>
              {reportDetails.conversation && <MessagesTab />}
            </Box>
            <Box display={selectedTab === componentTabs[2].value ? '' : 'none'}>
              <ProcessTemplateTab
                defaultValues={{
                  workflowTab: workflow ? `tab-${workflow}` : 'new-process',
                  workflowId: workflow,
                  selectedTaskId: taskId,
                }}
              />
            </Box>
            <Box display={selectedTab === componentTabs[3].value ? '' : 'none'}>
              <InvestigatorNotesTab />
            </Box>
            {selectedTab === componentTabs[4].value ? (
              <div className='h-full'>
                <Suspense fallback={null}>
                  <AuditLogs />
                </Suspense>
              </div>
            ) : null}
            {selectedTab === componentTabs[5].value ? (
              <div className='h-full'>
                <Suspense fallback={null}>
                  <RequestInformation />
                </Suspense>
              </div>
            ) : null}
            <Box
              sx={{ borderRight: '0px solid transparent' }}
              display={selectedTab === componentTabs[6].value ? '' : 'none'}
            >
              <InformedIndividualsTab />
            </Box>
          </Box>
        </Box>
      </Stack>
      <ToastAlert
        open={sendRequestModalState.open}
        setOpen={() => {
          const newSendRequestModalState = {
            ...sendRequestModalState,
            open: false,
          };
          dispatch(setSendRequest(newSendRequestModalState));
        }}
        message={sendRequestModalState.message}
        severity={sendRequestModalState.severity}
        duration={2000}
      />
    </Box>
  );
};

export default CustomerReportDocuments;
